import { apiGet, apiPost } from './utils';

export interface MissingTaskDetail {
  request_key: string;
  request_uuid: string;
  price: number;
  date_solved: string | null;
  solver_paid_date: string | null;
}

export interface PaymentDebugInfo {
  total_paid_tasks_in_range: number;
  payment_records_checked: number;
  payment_records_with_requests: number;
  total_request_keys_in_payments: number;
  recent_payments: Array<{
    name: string;
    date: string | null;
    base_pay: number;
    request_count: number;
  }>;
}

export interface BatchError {
  phase: string;
  user_key?: string;
  batch_start?: number;
  batch_size?: number;
  error: string;
}

export interface TaskPaymentSummaryUser {
  user_key: string;
  email: string;
  name: string;
  task_count: number;
  task_total: number;
  mentor_claim_count?: number;
  mentor_total?: number;
  total: number;
  debug?: PaymentDebugInfo;
  missing_tasks?: MissingTaskDetail[];
}

export interface SkippedUser {
  user_key: string;
  email: string;
  name: string;
  reason: string;
}

export interface TaskPaymentResult {
  dry_run: boolean;
  run_id?: string;
  batch_uuid?: string;
  csv: string;
  summary: TaskPaymentSummaryUser[];
  skipped_users: SkippedUser[];
  total_amount: number;
  request_count?: number;
  claim_count?: number;
  user_count: number;
  errors?: BatchError[];
}

export interface PaymentBatch {
  key: string;
  uuid: string;
  run_id: string;
  batch_type: 'run' | 'missing' | 'mentor-only' | 'repair-pay-via-shift';
  date_range_start: string | null;
  date_range_end: string | null;
  operator_email: string;
  status: 'processing' | 'completed' | 'partial' | 'voided';
  total_amount: number;
  user_count: number;
  request_count: number;
  mentor_claim_count: number;
  avg_per_user: number;
  max_payment: number;
  min_payment: number;
  skipped_user_count: number;
  payment_count: number;
  has_errors: boolean;
  date_created: string;
  date_completed: string | null;
  date_voided: string | null;
  voided_by_email: string | null;
}

export interface VoidBatchUser {
  user_key: string | null;
  email: string | null;
  name: string | null;
  // What the deleted payment record said would be sent
  amount: number;
  // What is actually being marked unpaid
  reverted_amount: number;
  request_count: number;
  mentor_claim_count: number;
}

export interface VoidBatchResult {
  dry_run: boolean;
  run_id: string;
  summary: VoidBatchUser[];
  total_amount: number;
  reverted_amount: number;
  user_count: number;
  request_count: number;
  mentor_claim_count: number;
  payment_count: number;
  // Users whose payment record failed to save during the run; void can't revert their tasks
  unrecorded_user_keys: string[];
}

export interface PaymentBatchDetail extends PaymentBatch {
  payment_keys: string[];
  errors: BatchError[];
}

export interface PaymentBatchListResponse {
  items: PaymentBatch[];
  cursor: string | null;
  has_more: boolean;
}

export interface PaidTaskPaymentRecord {
  payment_key: string;
  run_id: string | null;
  name: string | null;
  date_created: string | null;
}

// A request currently marked paid (from GET /task-payment/paid)
export interface PaidTask {
  request_key: string;
  request_uuid: string | null;
  title: string | null;
  price: number;
  solver_paid_amount: number;
  solver_paid_date: string | null;
  date_solved: string | null;
  status: number;
  source: number;
  pay_via_shift: boolean;
  solved_by: string | null;
  solver_name: string | null;
  solver_email: string | null;
  // The FHAdminPayment that lists this task; null means no record (e.g. its
  // batch was voided but the task stayed paid)
  payment_record: PaidTaskPaymentRecord | null;
}

export interface PaidTasksResult {
  tasks: PaidTask[];
  task_count: number;
  user_count: number;
  total_amount: number;
  // More than the server cap matched; narrow the range
  truncated: boolean;
  date_start: string;
  date_end: string;
}

export interface MarkUnpaidUser {
  user_key: string | null;
  name: string | null;
  email: string | null;
  request_count: number;
  reverted_amount: number;
}

export interface MarkUnpaidSkipped {
  request_key: string;
  request_uuid?: string | null;
  reason: string;
}

export interface MarkUnpaidResult {
  dry_run: boolean;
  summary: MarkUnpaidUser[];
  request_count: number;
  reverted_amount: number;
  user_count: number;
  skipped: MarkUnpaidSkipped[];
}

export const taskPaymentApi = {
  async runPayment(
    dateStart: string,
    dateEnd: string,
    dryRun: boolean = true
  ): Promise<TaskPaymentResult> {
    return apiPost<TaskPaymentResult>('/task-payment/run', {
      date_start: dateStart,
      date_end: dateEnd,
      dry_run: dryRun ? 'true' : 'false',
    });
  },

  async findMissing(
    dateStart: string,
    dateEnd: string,
    dryRun: boolean = true,
    lookback?: number,
  ): Promise<TaskPaymentResult> {
    const params: Record<string, string> = {
      date_start: dateStart,
      date_end: dateEnd,
      dry_run: dryRun ? 'true' : 'false',
    };
    if (lookback !== undefined) {
      params.lookback = String(lookback);
    }
    return apiPost<TaskPaymentResult>('/task-payment/missing', params);
  },

  async mentorOnly(
    dateStart: string,
    dateEnd: string,
    dryRun: boolean = true
  ): Promise<TaskPaymentResult> {
    return apiPost<TaskPaymentResult>('/task-payment/mentor-only', {
      date_start: dateStart,
      date_end: dateEnd,
      dry_run: dryRun ? 'true' : 'false',
    });
  },

  async listBatches(
    cursor?: string,
    limit?: number
  ): Promise<PaymentBatchListResponse> {
    const params: Record<string, string> = {};
    if (cursor) {
      params.cursor = cursor;
    }
    if (limit !== undefined) {
      params.limit = String(limit);
    }
    return apiGet<PaymentBatchListResponse>('/task-payment/batches', params);
  },

  // Undo a run whose CSV was never sent to Dwolla: marks its tasks and mentor
  // claims unpaid again. Defaults to dry-run (preview).
  async voidBatch(runId: string, dryRun: boolean = true): Promise<VoidBatchResult> {
    return apiPost<VoidBatchResult>('/task-payment/void', {
      run_id: runId,
      dry_run: dryRun ? 'true' : 'false',
    });
  },

  async getBatch(runId: string): Promise<PaymentBatchDetail> {
    return apiGet<PaymentBatchDetail>(`/task-payment/batch/${runId}`);
  },

  // Tasks marked paid with a paid date (solver_paid_date) in the range
  async listPaidTasks(dateStart: string, dateEnd: string): Promise<PaidTasksResult> {
    return apiGet<PaidTasksResult>('/task-payment/paid', {
      date_start: dateStart,
      date_end: dateEnd,
    });
  },

  // Mark specific tasks unpaid so the next run pays them. Defaults to dry-run (preview).
  async markUnpaid(requestKeys: string[], dryRun: boolean = true): Promise<MarkUnpaidResult> {
    return apiPost<MarkUnpaidResult>('/task-payment/mark-unpaid', {
      request_keys: requestKeys.join(','),
      dry_run: dryRun ? 'true' : 'false',
    });
  },
};
